import { useMemo } from 'react';

import { CampaignStoreItemTypes } from '../../types/utils';
import { useShowCampaignById_new } from '../get';

type CampaignSettings = ReturnType<typeof createCampaignSettings>;

const CAMPAIGN_TYPE_NAMING = {
  event: { lowercase: 'event', capitalized: 'Event' },
  campaign: { lowercase: 'crowdfunding', capitalized: 'Crowdfunding' },
  athon: { lowercase: 'athon', capitalized: 'A-thon' },
};

const createCampaignSettings = (data?: CampaignStoreItemTypes) => {
  const campaignTypeNaming =
    CAMPAIGN_TYPE_NAMING[data?.__type as keyof typeof CAMPAIGN_TYPE_NAMING] ??
    CAMPAIGN_TYPE_NAMING.campaign;

  return {
    isTeamsEnabled: data?.team_options?.enabled,
    isParticipantsEnabled: data?.participant_options?.enabled,
    isRegistrationOpen: data?.participant_options?.registration_open,
    isRegistrationByAdminOnly: data?.participant_options?.registration_by === 'admin_only',
    isParticipantsInCenterEnabled: data?.participant_options?.show_in_center,
    isTeamsInCenterEnabled: data?.team_options?.show_in_center,
    isParticipantLeaderboardInCenterEnabled: data?.participant_options?.show_on_leaderboard,
    isTeamLeaderboardInCenterEnabled: data?.team_options?.show_on_leaderboard,
    isPeersDisabled: !data?.team_options?.enabled && !data?.participant_options?.enabled,
    isPeersEnabled: data?.team_options?.enabled || data?.participant_options?.enabled,
    isTeamPrizesEnabled: data?.team_options?.prizes_enabled,
    isParticipantPrizesEnabled: data?.participant_options?.prizes_enabled,
    isBenefitsEnabled: data?.benefit_options?.enabled,
    isCampaignPublished: data?.status === 'published',
    isCampaignScheduled: data?.status === 'scheduled',
    isCampaignDraft: data?.status === 'draft',
    isCampaignFinished: data?.status === 'finished',
    isCampaignReadOnly: data?.status === 'finished' || data?.status === 'finishing_required',
    isCampaignDeleted: !!data?.deleted_at,
    type: data?.__type,
    campaignTypeNaming,
    isParticipantGoalEnabled: data?.participant_options?.goal_setting !== 'no_goal',
    isTeamGoalRequested: data?.team_options?.registration_options?.goal?.requested,
    isSingleDateEvent: data?.__type === 'event' && data?.single_date,
  };
};

/** Provides a singular structured response to formulate highly used evaluations */
const useShowCampaignSettings = ({
  causeId,
  campaignId,
}: {
  causeId?: string;
  campaignId?: string;
}) => {
  const {
    data: campaignSettings,
    isLoading,
    isSuccess,
    isFetching,
  } = useShowCampaignById_new({
    campaignId,
    causeId,
    select: createCampaignSettings,
  });

  // By checking if the query is even enabled, we can have another layer of checking and not strictly rely on isLoading
  const isQueryEnabled = useMemo(() => campaignId && causeId, [campaignId, causeId]);

  return useMemo(
    () => ({
      data: campaignSettings ?? createCampaignSettings(),
      isLoading,
      isEnabled: isQueryEnabled,
      isSuccess,
      isFetching,
    }),
    [campaignSettings, isLoading, isQueryEnabled, isSuccess, isFetching]
  );
};

export { createCampaignSettings, useShowCampaignSettings };
export type { CampaignSettings };
